// Custom implementation
/**
 * Creates a deep clone of the provided value, recursively copying all nested objects and arrays.
 *
 * @template T - The type of the value to clone.
 * @param {T} value - The value to deeply clone. Can be an object, array, or primitive.
 * @returns {T} A deep copy of the input value.
 *
 * @example
 * const original = { foo: 1, bar: { baz: 2 } };
 * const copy = cloneDeep(original);
 * copy.bar.baz = 3;
 * console.log(original.bar.baz); // 2
 */

export const cloneDeep = <T>(value: T): T => {
  if (value === null || typeof value !== "object") {
    return value // Return primitive values as is
  }

  if (Array.isArray(value)) {
    return (value as Array<unknown>).map(item => cloneDeep(item)) as T // Recursively clone array items
  }

  const result: Record<string, unknown> = {}
  for (const key in value) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      result[key] = cloneDeep((value as Record<string, unknown>)[key]) // Recursively clone object properties
    }
  }

  return result as T // Return the cloned object
}
